// components/ApiModeExplainer.tsx
import { motion } from "framer-motion";
import {
  Info,
  Mic,
  MessageSquare,
  Zap,
  Clock,
  Volume2,
  Wifi,
  ArrowRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import React, { useState } from "react";
import { Card, CardContent, CardHeader, Button } from "./ui";

interface ApiModeExplainerProps {
  isLiveAPIConnected: boolean;
  aiProvider: "openai" | "gemini";
}

const ApiModeExplainer: React.FC<ApiModeExplainerProps> = ({
  isLiveAPIConnected,
  aiProvider,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const standardApiFlow = [
    { icon: Mic, label: "Voice Input", description: "Record audio" },
    {
      icon: MessageSquare,
      label: "Transcription",
      description: "Speech → Text",
    },
    { icon: Zap, label: "AI Processing", description: "Text → Response" },
    { icon: Volume2, label: "Text-to-Speech", description: "Text → Audio" },
  ];

  const liveApiFlow = [
    { icon: Mic, label: "Voice Stream", description: "Real-time audio" },
    {
      icon: Zap,
      label: "AI Processing",
      description: "Direct audio processing",
    },
    { icon: Volume2, label: "Voice Response", description: "Natural speech" },
  ];

  return (
    <Card className="mb-6 bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Info className="h-5 w-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">
              API Mode: {isLiveAPIConnected ? "Live API" : "Standard API"}
            </h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Hide Details" : "Learn More"}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* Current Mode Status */}
            <div className="flex items-center justify-center p-4 rounded-lg bg-slate-700/50">
              <div className="text-center">
                {isLiveAPIConnected ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-green-400">
                      <Wifi className="h-6 w-6" />
                      <span className="font-semibold">Live API Active</span>
                    </div>
                    <p className="text-sm text-slate-300">
                      Real-time voice streaming with low latency
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-center space-x-2 text-blue-400">
                      <MessageSquare className="h-6 w-6" />
                      <span className="font-semibold">Standard API Active</span>
                    </div>
                    <p className="text-sm text-slate-300">
                      Voice transcription + text processing + TTS
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Comparison Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Standard API */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-4 rounded-lg border-2 transition-all ${
                  !isLiveAPIConnected
                    ? "border-blue-400 bg-blue-400/10"
                    : "border-slate-600 bg-slate-700/30"
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <MessageSquare className="h-5 w-5 text-blue-400" />
                  <h4 className="font-semibold text-white">Standard API</h4>
                  {!isLiveAPIConnected && (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  )}
                </div>

                {/* Flow Diagram */}
                <div className="space-y-2 mb-4">
                  {standardApiFlow.map((step, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <step.icon className="h-4 w-4 text-slate-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">
                          {step.label}
                        </div>
                        <div className="text-xs text-slate-400">
                          {step.description}
                        </div>
                      </div>
                      {index < standardApiFlow.length - 1 && (
                        <ArrowRight className="h-3 w-3 text-slate-500" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-slate-300">
                    Features:
                  </h5>
                  <ul className="space-y-1 text-xs text-slate-400">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Works with all providers</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Reliable transcription</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <Clock className="h-3 w-3 text-yellow-400" />
                      <span>Higher latency (2-5 seconds)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <XCircle className="h-3 w-3 text-red-400" />
                      <span>No interruption support</span>
                    </li>
                  </ul>
                </div>
              </motion.div>

              {/* Live API */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isLiveAPIConnected
                    ? "border-green-400 bg-green-400/10"
                    : "border-slate-600 bg-slate-700/30"
                }`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Wifi className="h-5 w-5 text-green-400" />
                  <h4 className="font-semibold text-white">Live API</h4>
                  {isLiveAPIConnected && (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  )}
                  {aiProvider !== "gemini" && (
                    <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded">
                      Gemini Only
                    </span>
                  )}
                </div>

                {/* Flow Diagram */}
                <div className="space-y-2 mb-4">
                  {liveApiFlow.map((step, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <step.icon className="h-4 w-4 text-slate-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">
                          {step.label}
                        </div>
                        <div className="text-xs text-slate-400">
                          {step.description}
                        </div>
                      </div>
                      {index < liveApiFlow.length - 1 && (
                        <ArrowRight className="h-3 w-3 text-slate-500" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-slate-300">
                    Features:
                  </h5>
                  <ul className="space-y-1 text-xs text-slate-400">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Ultra-low latency (&lt;500ms)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Natural voice interruptions</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Emotion-aware responses</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="h-3 w-3 text-green-400" />
                      <span>Real-time audio streaming</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <XCircle className="h-3 w-3 text-red-400" />
                      <span>Gemini provider only</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            </div>

            {/* Technical Details */}
            <div className="p-4 bg-slate-700/30 rounded-lg">
              <h5 className="font-medium text-white mb-2">
                Technical Differences:
              </h5>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h6 className="font-medium text-blue-400 mb-1">
                    Standard API Process:
                  </h6>
                  <ol className="space-y-1 text-slate-400 list-decimal list-inside">
                    <li>Record audio using MediaRecorder</li>
                    <li>Send audio blob to transcription service</li>
                    <li>Send transcribed text to AI model</li>
                    <li>Convert AI response to speech using TTS</li>
                    <li>Play generated audio</li>
                  </ol>
                </div>
                <div>
                  <h6 className="font-medium text-green-400 mb-1">
                    Live API Process:
                  </h6>
                  <ol className="space-y-1 text-slate-400 list-decimal list-inside">
                    <li>Open WebSocket connection to Live API</li>
                    <li>Stream audio data in real-time</li>
                    <li>Receive audio responses directly</li>
                    <li>Support for mid-conversation interruptions</li>
                    <li>Native multimodal processing</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Usage Recommendations */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h5 className="font-medium text-blue-300 mb-2">
                When to Use Each Mode:
              </h5>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h6 className="font-medium text-white mb-1">
                    Use Standard API for:
                  </h6>
                  <ul className="space-y-1 text-slate-300">
                    <li>• OpenAI GPT models</li>
                    <li>• Simple voice commands</li>
                    <li>• Non-real-time interactions</li>
                    <li>• Better transcription accuracy</li>
                    <li>• Debugging and development</li>
                  </ul>
                </div>
                <div>
                  <h6 className="font-medium text-white mb-1">
                    Use Live API for:
                  </h6>
                  <ul className="space-y-1 text-slate-300">
                    <li>• Natural conversations</li>
                    <li>• Real-time assistance</li>
                    <li>• Voice-first applications</li>
                    <li>• Interactive demonstrations</li>
                    <li>• Maximum responsiveness</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ApiModeExplainer;
